import type { AppPlan, PlanScreen } from '@/webapp/plan/planTypes';
import type { LayoutOption } from '@/llm/planTypes';

/**
 * The layout step's decisions, apart from the screen that paints them.
 *
 * Pure for the same reason `wireframeModel` is: every rule here was broken once in a way that
 * rendered fine. Options proposed for features somebody had since removed, screens handed to
 * the generator with no purpose, a chosen layout erased by "Apply" while the card still said
 * Chosen — each looked right on screen and built something else.
 */

/**
 * What a layout proposal is built FROM — the parts of the plan the person edits on the
 * features step.
 *
 * Screens are left out on purpose: committing a layout rewrites them, so a key that included
 * them would change the moment the proposal landed and ask for another one.
 */
export function layoutInputsKey(plan: AppPlan): string {
  return JSON.stringify({
    f: plan.features.filter((f) => f.included).map((f) => f.label.trim()),
    k: plan.keeps,
    c: plan.collects,
    m: plan.manages ?? [],
  });
}

export type LayoutArrival =
  /** Signed out: `/llm` refuses anonymous callers, so the call could only fail. */
  | 'skip'
  /** Proposed — or being proposed right now — for exactly these inputs. */
  | 'keep'
  /** Nothing proposed yet, or the last attempt failed. */
  | 'fetch'
  /** The features changed since: the options on hand are for a different app. */
  | 'refetch';

/**
 * What to do when somebody arrives at the layout step.
 *
 * It used to ask once per MOUNT: the screen never unmounts between steps, so going Back,
 * removing "Stats" and returning showed the same Stats-shaped options, and a failure stayed
 * failed for the life of the screen. An arrival is the unit — the features step is where the
 * inputs change, and this is where they are read.
 */
export function onLayoutArrival(s: {
  signedIn: boolean;
  key: string;
  /** The inputs the options on hand (or in flight) were requested for; null if none. */
  proposedFor: string | null;
  failed: boolean;
}): LayoutArrival {
  if (!s.signedIn) return 'skip';
  if (s.proposedFor === null) return 'fetch';
  if (s.proposedFor !== s.key) return 'refetch';
  return s.failed ? 'fetch' : 'keep';
}

const norm = (s: string) => s.trim().toLowerCase();

/**
 * Put the chosen arrangement on the plan, which is the document the generator reads.
 *
 * `planned` is the screens the PLANNER proposed, before any arrangement renamed them. Purposes
 * are looked up there rather than in the plan's current screens, because after the first
 * commit the current screens ARE the previous option's: looking there lost the planner's
 * purposes for good, and every screen the option named differently reached the build prompt
 * as "- Progress: " — a screen to build and nothing about what it is for.
 *
 * Order: what the option itself says the screen is for, then the planner's purpose for a
 * screen of that name (case and spacing ignored), then the current screens', then the label
 * itself — the same last resort the plan's own normaliser uses, so no screen is ever blank.
 */
export function withLayout(
  plan: AppPlan,
  opt: LayoutOption,
  tweak: string,
  planned: PlanScreen[],
): AppPlan {
  const purposeOf = (label: string, i: number): string => {
    const own = opt.purposes?.[i]?.trim();
    if (own) return own;
    const hit = planned.find((s) => norm(s.label) === norm(label))
      ?? plan.screens.find((s) => norm(s.label) === norm(label));
    return hit?.purpose.trim() || label;
  };
  return {
    ...plan,
    layout: {
      title: opt.title,
      blueprint: opt.blueprint,
      nav: opt.nav,
      ...(opt.tabs?.length ? { tabs: opt.tabs } : {}),
      mainLayout: opt.mainLayout,
      ...(tweak.trim() ? { tweak: tweak.trim() } : {}),
    },
    // The option names the screens for THIS arrangement; keeping the old ones would build a
    // tabbed app whose tabs are named after a drill-down it is not.
    ...(opt.screens.length
      ? {
        screens: opt.screens.map((label, i) => ({
          id: plan.screens[i]?.id ?? `screen-${i + 1}`,
          label,
          purpose: purposeOf(label, i),
        })),
      }
      : {}),
  };
}

/**
 * "Apply" on the tailored-plan banner.
 *
 * The tailored plan always arrives with `layout: null` — the server planner never chooses an
 * arrangement — so applying it as it stood erased the layout and tweak the person had picked,
 * while the card still said Chosen and the tweak still sat in its box. Whatever is on screen
 * must be what gets built, so the chosen arrangement is laid back over the tailored plan, and
 * its screens take their purposes from the tailored planner.
 *
 * But only while the options still describe the plan. Next is not held for the tailoring
 * pass, so a quick person reaches this step with the scaffold and gets options proposed for
 * the SCAFFOLD's features; the tailored plan then arrives with different ones. Laying the old
 * option over it built an arrangement for a feature set that no longer existed — the defect
 * `onLayoutArrival` closes for Back-and-edit, reached through Apply instead. So when the
 * inputs the options were asked for (`proposedFor`) are not the tailored plan's, the option is
 * NOT applied and `keepOptions` is false: the screen drops them and asks again.
 */
export function applyTailored(
  suggestion: AppPlan,
  onHand: { option: LayoutOption | null; tweak: string; proposedFor: string | null },
): { plan: AppPlan; keepOptions: boolean } {
  const keepOptions = onHand.proposedFor !== null && onHand.proposedFor === layoutInputsKey(suggestion);
  if (!keepOptions || !onHand.option) return { plan: suggestion, keepOptions };
  return {
    plan: withLayout(suggestion, onHand.option, onHand.tweak, suggestion.screens),
    keepOptions,
  };
}

/** The feature that carries the "Anything else" box when there is no arrangement to carry it. */
export const ANYTHING_ELSE_ID = 'anything-else';

/**
 * Put the words from the "Anything else" box on the plan, whatever the layout call did.
 *
 * They ride on the chosen arrangement as `layout.tweak` when there is one — `renderPlan`
 * quotes it beside the arrangement. The box used to be shown ONLY beside an arrangement for
 * that reason, and a signed-out visitor (whose layout call is never made) or anyone whose call
 * failed never saw it, while the step's subtitle told them to "say anything else you want".
 *
 * With no arrangement the words become a feature of their own, in the person's words. The
 * features block is what the generator is told must all work, so they reach it verbatim; and
 * a build that fails hands the plan back through the store, where they show up on the
 * features step as something the person can see, edit or remove — not a hidden field. One
 * fixed id, so building again replaces the line instead of adding a second.
 *
 * Blank words change nothing: a blank box after a remount is not a request to delete what an
 * earlier attempt put on the plan.
 */
export function withAnythingElse(plan: AppPlan, words: string): AppPlan {
  const said = words.trim();
  if (!said) return plan;
  if (plan.layout) return { ...plan, layout: { ...plan.layout, tweak: said } };
  return {
    ...plan,
    features: [
      ...plan.features.filter((f) => f.id !== ANYTHING_ELSE_ID),
      { id: ANYTHING_ELSE_ID, label: 'Anything else', detail: said, included: true, origin: 'user' },
    ],
  };
}
